import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { useState } from "react"
import GameScreenLayout from "@/components/GameScreenLayout"
import {
  cancelDailyTournament,
  getDailyTournamentCards,
  getDailyTournamentCurrent,
  registerDailyTournament,
} from "@/api/dailyVirtualTournamentApi"
import { ColorRank } from "@/types/battle"
import { RANK_NAMES } from "@/constants/guardians"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"

const RANKS: ColorRank[] = ["WHITE", "GREEN", "BLUE", "PURPLE", "ORANGE", "RED"]

export default function DailyTournamentPage() {
  const queryClient = useQueryClient()
  const [rank, setRank] = useState<ColorRank>("WHITE")
  const [cardId, setCardId] = useState("")

  const currentQuery = useQuery({
    queryKey: ["dvt-current"],
    queryFn: getDailyTournamentCurrent,
  })

  const cardsQuery = useQuery({
    queryKey: ["dvt-cards", rank],
    queryFn: () => getDailyTournamentCards(rank),
  })

  const register = useMutation({
    mutationFn: () => registerDailyTournament({ colorRank: rank, cardId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dvt-current"] })
    },
  })

  const cancel = useMutation({
    mutationFn: () => cancelDailyTournament({ colorRank: rank }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dvt-current"] })
    },
  })

  const current = currentQuery.data

  return (
    <GameScreenLayout title="Ежедневный турнир" backTo="/play">
      {currentQuery.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : current ? (
        <div className="space-y-2 text-sm">
          <p>
            День {current.dayStart} ·{" "}
            {current.registrationOpen ? "регистрация открыта" : "закрыта"}
          </p>
          <ul className="space-y-1">
            {current.ranks.map((r) => (
              <li
                key={r.colorRank}
                className="flex items-center justify-between gap-2 text-guardians-muted"
              >
                <span>{RANK_NAMES[r.colorRank]}</span>
                <span>
                  {r.participantCount} · {r.status}
                </span>
                {r.tournamentId ? (
                  <Link
                    to={`/play/daily-tournament/${encodeURIComponent(r.tournamentId)}?colorRank=${r.colorRank}`}
                    className="shrink-0 text-xs text-guardians-accent underline"
                  >
                    Сетка
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-guardians-muted">Нет турнира на сегодня</p>
      )}

      <section className="mt-6 space-y-2">
        <select
          value={rank}
          onChange={(e) => setRank(e.target.value as ColorRank)}
          className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
        >
          {RANKS.map((r) => (
            <option key={r} value={r}>
              {RANK_NAMES[r]}
            </option>
          ))}
        </select>
        <select
          value={cardId}
          onChange={(e) => setCardId(e.target.value)}
          className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
        >
          <option value="">Карта</option>
          {(cardsQuery.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.element} Lv.{c.level}
            </option>
          ))}
        </select>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!cardId || register.isPending}
            onClick={() => register.mutate()}
            className="flex-1 rounded-xl bg-guardians-accent py-2 text-guardians-bg"
          >
            Записаться
          </button>
          <button
            type="button"
            onClick={() => cancel.mutate()}
            className="rounded-xl border border-white/20 px-4 py-2"
          >
            Отмена
          </button>
        </div>
      </section>

      {(register.isError || cancel.isError) && (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(register.error ?? cancel.error, "Ошибка")}
        </p>
      )}
    </GameScreenLayout>
  )
}
